/**
 * Global search sort (Meilisearch); must stay aligned with App\Services\Search\SearchSortOptions.
 */
export const SEARCH_SORT_RELEVANCE = 'relevance';

/** Same text as `SearchSortOptions::SORT_FALLBACK_MESSAGE` (API meta.sort_fallback_message). */
export const SEARCH_SORT_FALLBACK_MESSAGE = 'Sorting is unavailable for this query. Results are shown by relevance.';

/**
 * Enforce relevance + desc; normalize direction to asc|desc.
 *
 * @param {string} sortBy
 * @param {string} sortDir
 * @returns {{ sortBy: string, sortDir: 'asc' | 'desc' }}
 */
export function normalizeSearchSortState(sortBy, sortDir) {
    if (!sortBy || sortBy === SEARCH_SORT_RELEVANCE) {
        return { sortBy: SEARCH_SORT_RELEVANCE, sortDir: 'desc' };
    }
    return { sortBy, sortDir: sortDir === 'asc' ? 'asc' : 'desc' };
}

/**
 * Mutates request params for GET /api/search-results (initial fetch + load-more).
 * Always sets normalized `sort` and `sort_direction` (including relevance → desc).
 *
 * @param {Record<string, unknown>} params
 * @param {string} sortBy
 * @param {string} sortDir
 */
export function appendSortToSearchParams(params, sortBy, sortDir) {
    const { sortBy: by, sortDir: dir } = normalizeSearchSortState(sortBy, sortDir);
    params.sort = by;
    params.sort_direction = dir;
}

/**
 * Mutates the `search-results` route query: omits sort keys for relevance; otherwise adds
 * normalized `sort` and `sort_direction`.
 *
 * @param {Record<string, unknown>} queryParams
 * @param {string} sortBy
 * @param {string} sortDir
 */
export function mergeSearchResultsSortIntoUrlQuery(queryParams, sortBy, sortDir) {
    const { sortBy: by, sortDir: dir } = normalizeSearchSortState(sortBy, sortDir);
    if (by && by !== SEARCH_SORT_RELEVANCE) {
        queryParams.sort = by;
        queryParams.sort_direction = dir;
    }
}

/**
 * Sort dropdown options; direction (asc/desc) is controlled separately (see API sort_direction).
 *
 * @returns {Array<{ value: string, label: string }>}
 */
export function buildSearchSortSelectOptions() {
    return [
        { value: 'relevance', label: 'Relevance' },
        { value: 'created_at', label: 'Last Created' },
        { value: 'updated_at', label: 'Last Edited' },
    ];
}

/**
 * Accessible label for the direction toggle (arrow button).
 *
 * @param {string} sortBy
 * @param {string} sortDir
 * @returns {string}
 */
export function getSortDirectionAriaLabel(sortBy, sortDir) {
    if (!sortBy || sortBy === SEARCH_SORT_RELEVANCE) {
        return 'Sort direction not applicable for relevance';
    }
    return sortDir === 'desc' ? 'Newest first' : 'Oldest first';
}
